import type { ResolvedPriority } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { getSenderPriorityTranslation } from "@/utils/translations";
import type { StatusTranslation } from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	Icon,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	Typography,
} from "@st-marketplace/ui";

interface PriorityCellProps {
	resolved: ResolvedPriority | null;
}

type SourceType = ResolvedPriority["type"];

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const PriorityCell: React.FC<PriorityCellProps> = ({ resolved }) => {
	const { t } = useTranslation();

	if (!resolved) {
		return <Typography variant="body-sm">-</Typography>;
	}

	const sourceConfig: Record<
		SourceType,
		{ label: string; tooltip: string; variant: ChipVariant }
	> = {
		SENDER: {
			label: t("sender.resolvedPriorityLabelSender"),
			tooltip: t("sender.resolvedPriorityTooltipSender"),
			variant: "info",
		},
		SENDER_GROUP: {
			label: t("sender.resolvedPriorityLabelGroup"),
			tooltip: t("sender.resolvedPriorityTooltipGroup"),
			variant: "info",
		},
		GLOBAL: {
			label: t("sender.resolvedPriorityLabelGlobal"),
			tooltip: t("sender.resolvedPriorityTooltipGlobal"),
			variant: "muted",
		},
	};

	const config = sourceConfig[resolved.type];
	const priorityLabel = getSenderPriorityTranslation(resolved.value, t);

	return (
		<span className="inline-flex items-center gap-1">
			<Chip
				variant={STATUS_COLOR_TO_CHIP_VARIANT[priorityLabel.color]}
				size="sm"
			>
				{priorityLabel.text}
			</Chip>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="inline-flex cursor-help">
						<Chip variant={config.variant} size="sm">
							<span className="inline-flex items-center gap-0.5">
								{config.label}
								<Icon
									name="information-line"
									size="xs"
									className="opacity-70"
								/>
							</span>
						</Chip>
					</span>
				</TooltipTrigger>
				<TooltipContent>
					<p>{config.tooltip}</p>
					<p>{t("sender.resolvedPriorityTooltipHint")}</p>
				</TooltipContent>
			</Tooltip>
		</span>
	);
};

export default PriorityCell;
